import type { FieldFeature, SurveyBlock, Ward, Zone } from "./types";

/**
 * Narrowing the field-data table to one zone, ward or block.
 *
 * Done in the browser, not by the API. `GET /features` takes `project_id` OR
 * `block_id` and nothing between, so filtering by zone or ward server-side
 * would mean new query parameters and a join per request; meanwhile the
 * workspace page already holds every feature of the project AND the whole
 * hierarchy, fetched for the map's own boundary layers. Everything needed is
 * therefore already in memory, and filtering it is a set lookup per row.
 *
 * The hierarchy is what makes this possible: a feature carries only
 * `block_id`, so "which zone is this in" is a block -> ward -> zone walk that
 * needs the three lists to answer.
 */

/** The three levels, as the workspace page already fetches them. */
export type Hierarchy = {
  zones: Zone[];
  wards: Ward[];
  blocks: SurveyBlock[];
};

export const EMPTY_HIERARCHY: Hierarchy = { zones: [], wards: [], blocks: [] };

/** A chosen level, or null at any level for "all of them". */
export type HierarchyFilter = {
  zoneId: number | null;
  wardId: number | null;
  blockId: number | null;
};

export const NO_FILTER: HierarchyFilter = {
  zoneId: null,
  wardId: null,
  blockId: null,
};

export function isFiltered(filter: HierarchyFilter): boolean {
  return filter.zoneId !== null || filter.wardId !== null || filter.blockId !== null;
}

/**
 * The block ids a filter admits, or null meaning "no filter, everything".
 *
 * Null and an empty set are deliberately different answers: null is "do not
 * filter", an empty set is "filter matched no block at all", and collapsing
 * the two would turn a ward with no blocks into a table showing every feature
 * in the project.
 *
 * The narrowest chosen level wins outright. A block id already names one
 * block, so the zone and ward above it can only agree with it or contradict
 * it, and a contradiction can't be produced through the UI anyway (picking a
 * zone clears a ward beneath a different one -- see `reconcile`).
 */
export function allowedBlockIds(
  hierarchy: Hierarchy,
  filter: HierarchyFilter
): Set<number> | null {
  const { zoneId, wardId, blockId } = filter;
  if (!isFiltered(filter)) return null;

  if (blockId !== null) return new Set([blockId]);

  const wardIds =
    wardId !== null
      ? new Set([wardId])
      : new Set(
          hierarchy.wards.filter((w) => w.zone_id === zoneId).map((w) => w.id)
        );

  return new Set(
    hierarchy.blocks.filter((b) => wardIds.has(b.ward_id)).map((b) => b.id)
  );
}

/**
 * The features a filter admits.
 *
 * A feature with no `block_id` is dropped as soon as ANY level is chosen.
 * Those are project-scoped shapes, attached to the campaign rather than to a
 * block, so they sit in no zone and no ward: there is no level at which
 * "inside this ward" is true of them. Keeping them visible under a ward filter
 * would be claiming they belong to a ward nobody put them in.
 */
export function filterFeatures(
  features: FieldFeature[],
  hierarchy: Hierarchy,
  filter: HierarchyFilter
): FieldFeature[] {
  const allowed = allowedBlockIds(hierarchy, filter);
  if (allowed === null) return features;
  return features.filter((f) => f.block_id !== null && allowed.has(f.block_id));
}

// --- what each dropdown should offer, given the ones above it ---

export function wardsFor(hierarchy: Hierarchy, zoneId: number | null): Ward[] {
  return zoneId === null
    ? hierarchy.wards
    : hierarchy.wards.filter((w) => w.zone_id === zoneId);
}

export function blocksFor(
  hierarchy: Hierarchy,
  zoneId: number | null,
  wardId: number | null
): SurveyBlock[] {
  if (wardId !== null) return hierarchy.blocks.filter((b) => b.ward_id === wardId);
  if (zoneId === null) return hierarchy.blocks;
  const wardIds = new Set(wardsFor(hierarchy, zoneId).map((w) => w.id));
  return hierarchy.blocks.filter((b) => wardIds.has(b.ward_id));
}

/**
 * Drops any selection that no longer sits under the one above it.
 *
 * Call this after changing a level rather than clearing children by hand at
 * every call site. Choosing a zone while a ward in a DIFFERENT zone is
 * selected would otherwise leave a filter that reads "Zone A, Ward 12 of zone
 * B" -- the dropdowns would each show a value while the table showed nothing,
 * with no way to see which pair was contradictory.
 *
 * Widening is not narrowing: clearing the zone back to "All" keeps a ward
 * selection, because that ward is still a real ward and still under some zone.
 */
export function reconcile(
  hierarchy: Hierarchy,
  filter: HierarchyFilter
): HierarchyFilter {
  let { zoneId, wardId, blockId } = filter;

  if (wardId !== null && zoneId !== null) {
    const ward = hierarchy.wards.find((w) => w.id === wardId);
    if (ward && ward.zone_id !== zoneId) wardId = null;
  }

  if (blockId !== null) {
    const block = hierarchy.blocks.find((b) => b.id === blockId);
    const stillUnder = block
      ? blocksFor(hierarchy, zoneId, wardId).some((b) => b.id === block.id)
      : false;
    if (!stillUnder) blockId = null;
  }

  return { zoneId, wardId, blockId };
}

/** "Ward 12B" / "Zone 1 · Ward 12B" etc., for the "showing N of M" line. */
export function describeFilter(
  hierarchy: Hierarchy,
  filter: HierarchyFilter
): string {
  const parts: string[] = [];
  const zone = hierarchy.zones.find((z) => z.id === filter.zoneId);
  const ward = hierarchy.wards.find((w) => w.id === filter.wardId);
  const block = hierarchy.blocks.find((b) => b.id === filter.blockId);
  if (zone) parts.push(zone.name);
  if (ward) parts.push(ward.name);
  if (block) parts.push(block.name);
  return parts.join(" · ");
}

/** Zone / ward / block names for one feature, each null when unknown. */
export type FeatureAncestry = {
  zone: string | null;
  ward: string | null;
  block: string | null;
};

/**
 * Where a collected shape sits in the hierarchy, as names.
 *
 * Prefers the feature's own `zone_id` / `ward_id` -- denormalised columns the
 * backend fills at insert time -- and falls back to walking up from
 * `block_id` when they are absent. That fallback is not belt-and-braces: rows
 * created before deploy/add-feature-zone-ward.sql was applied have those
 * columns NULL, and without it every one of them would render three blank
 * cells, which reads as a shape belonging to nothing rather than as a column
 * that has not been backfilled yet.
 *
 * Names, not ids, because a table showing "Zone #3016" tells the office
 * nothing. Resolved from the hierarchy the page already holds for its own
 * filters, so this costs no request.
 */
export function ancestryOf(
  feature: Pick<FieldFeature, "block_id" | "zone_id" | "ward_id">,
  hierarchy: Hierarchy
): FeatureAncestry {
  const block =
    feature.block_id == null
      ? undefined
      : hierarchy.blocks.find((b) => b.id === feature.block_id);

  const wardId = feature.ward_id ?? block?.ward_id ?? null;
  const ward =
    wardId === null ? undefined : hierarchy.wards.find((w) => w.id === wardId);

  const zoneId = feature.zone_id ?? ward?.zone_id ?? null;
  const zone =
    zoneId === null ? undefined : hierarchy.zones.find((z) => z.id === zoneId);

  return {
    zone: zone?.name ?? null,
    ward: ward?.name ?? null,
    block: block?.name ?? null,
  };
}
